// Toast Notification Component
// Used for displaying kick vote notifications and other alerts

let toastContainer = null;
let activeToasts = new Map();

/**
 * Initialize toast container
 */
function initToastContainer() {
  if (!toastContainer) {
    toastContainer = document.createElement('div');
    toastContainer.className = 'toast-container';
    document.body.appendChild(toastContainer);
  }
  return toastContainer;
}

/**
 * Show a toast notification
 * @param {Object} options - Toast configuration
 * @returns {string} Toast ID for updates
 */
export function showToast(options) {
  const {
    title,
    message,
    type = 'default', // 'default', 'kick-vote', 'success', 'failed'
    duration = null, // null = no auto-dismiss
    id = generateToastId()
  } = options;

  const container = initToastContainer();

  // Remove existing toast with same ID if it exists
  if (activeToasts.has(id)) {
    removeToast(id);
  }

  // Create toast element
  const toast = document.createElement('div');
  toast.className = `toast ${type}`;
  toast.dataset.toastId = id;

  // Title
  if (title) {
    const titleEl = document.createElement('div');
    titleEl.className = 'toast-title';
    titleEl.textContent = title;
    toast.appendChild(titleEl);
  }

  // Message
  if (message) {
    const messageEl = document.createElement('div');
    messageEl.className = 'toast-message';
    messageEl.textContent = message;
    toast.appendChild(messageEl);
  }

  // Add to container
  container.appendChild(toast);

  // Store reference
  activeToasts.set(id, {
    element: toast,
    data: options
  });

  // Auto-dismiss if duration specified
  if (duration) {
    setTimeout(() => {
      removeToast(id);
    }, duration);
  }

  return id;
}

/**
 * Update an existing toast
 */
export function updateToast(id, updates) {
  const toastData = activeToasts.get(id);
  if (!toastData) return;

  const toast = toastData.element;

  // Update title
  if (updates.title !== undefined) {
    let titleEl = toast.querySelector('.toast-title');
    if (titleEl) {
      titleEl.textContent = updates.title;
    } else {
      titleEl = document.createElement('div');
      titleEl.className = 'toast-title';
      titleEl.textContent = updates.title;
      toast.insertBefore(titleEl, toast.firstChild);
    }
  }

  // Update message
  if (updates.message !== undefined) {
    let messageEl = toast.querySelector('.toast-message');
    if (messageEl) {
      messageEl.textContent = updates.message;
    } else {
      messageEl = document.createElement('div');
      messageEl.className = 'toast-message';
      messageEl.textContent = updates.message;
      toast.appendChild(messageEl);
    }
  }

  // Update type
  if (updates.type !== undefined) {
    toast.className = `toast ${updates.type}`;
  }

  // Update stored data
  toastData.data = { ...toastData.data, ...updates };
}

/**
 * Show kick vote toast with countdown
 */
export function showKickVoteToast(options) {
  const {
    playerName,
    duration = 20,
    targetPlayerId,
    currentVotes = 0,
    requiredVotes = 1,
    onVote = null,
    isInitiator = false,
    isTarget = false
  } = options;

  const id = `kick-vote-${targetPlayerId}`;

  const toast = document.createElement('div');
  toast.className = 'toast kick-vote';
  toast.dataset.toastId = id;

  // Title
  const titleEl = document.createElement('div');
  titleEl.className = 'toast-title';
  titleEl.textContent = isTarget ? 'Kick Vote Against You' : `Kick Vote: ${playerName}`;
  toast.appendChild(titleEl);

  // Message
  const messageEl = document.createElement('div');
  messageEl.className = 'toast-message';
  if (isTarget) {
    messageEl.textContent = 'A vote to kick you from the room has been initiated.';
  } else if (isInitiator) {
    messageEl.textContent = `You initiated a vote to kick ${playerName}.`;
  } else {
    messageEl.textContent = `A vote to kick ${playerName} has been initiated.`;
  }
  toast.appendChild(messageEl);

  // Vote buttons (only show for non-initiators and non-targets)
  let yesBtn = null;
  let noBtn = null;

  if (!isInitiator && !isTarget && onVote) {
    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'toast-vote-buttons';

    yesBtn = document.createElement('button');
    yesBtn.className = 'toast-vote-btn yes';
    yesBtn.textContent = 'Yes';
    yesBtn.onclick = () => {
      onVote(targetPlayerId, true);
      // Disable both buttons after voting
      yesBtn.disabled = true;
      noBtn.disabled = true;
      yesBtn.classList.add('voted');
      noBtn.classList.add('disabled');
    };

    noBtn = document.createElement('button');
    noBtn.className = 'toast-vote-btn no';
    noBtn.textContent = 'No';
    noBtn.onclick = () => {
      onVote(targetPlayerId, false);
      // Disable both buttons after voting
      yesBtn.disabled = true;
      noBtn.disabled = true;
      noBtn.classList.add('voted');
      yesBtn.classList.add('disabled');
    };

    buttonContainer.appendChild(yesBtn);
    buttonContainer.appendChild(noBtn);
    toast.appendChild(buttonContainer);
  }

  // Votes counter
  const votesEl = document.createElement('div');
  votesEl.className = 'toast-votes';
  votesEl.textContent = `Yes: ${currentVotes}/${requiredVotes}`;
  toast.appendChild(votesEl);

  // Countdown
  const countdownEl = document.createElement('div');
  countdownEl.className = 'toast-countdown';
  countdownEl.textContent = `${duration}s`;
  toast.appendChild(countdownEl);

  // Progress bar
  const progressEl = document.createElement('div');
  progressEl.className = 'toast-progress danger';
  const progressBar = document.createElement('div');
  progressBar.className = 'toast-progress-bar';
  progressBar.style.width = '100%';
  progressEl.appendChild(progressBar);
  toast.appendChild(progressEl);

  const container = initToastContainer();
  container.appendChild(toast);

  // Store reference
  activeToasts.set(id, {
    element: toast,
    data: options,
    countdownEl,
    votesEl,
    progressBar,
    yesBtn,
    noBtn,
    startTime: Date.now(),
    duration: duration * 1000
  });

  // Start countdown
  startKickVoteCountdown(id);

  return id;
}

/**
 * Start countdown for kick vote
 */
function startKickVoteCountdown(id) {
  const toastData = activeToasts.get(id);
  if (!toastData) return;

  const updateInterval = setInterval(() => {
    if (!activeToasts.has(id)) {
      clearInterval(updateInterval);
      return;
    }

    const elapsed = Date.now() - toastData.startTime;
    const remaining = Math.max(0, toastData.duration - elapsed);
    const seconds = Math.ceil(remaining / 1000);

    // Update countdown
    toastData.countdownEl.textContent = `${seconds}s`;

    // Update progress bar
    const progress = (remaining / toastData.duration) * 100;
    toastData.progressBar.style.width = `${progress}%`;

    // End if time's up
    if (remaining === 0) {
      clearInterval(updateInterval);
    }
  }, 100);

  // Store interval for cleanup
  toastData.interval = updateInterval;
}

/**
 * Update kick vote toast
 */
export function updateKickVoteToast(id, yesVotes, requiredVotes) {
  const toastData = activeToasts.get(id);
  if (!toastData) return;

  toastData.votesEl.textContent = `Yes: ${yesVotes}/${requiredVotes}`;
}

/**
 * Remove a toast
 */
export function removeToast(id) {
  const toastData = activeToasts.get(id);
  if (!toastData) return;

  const toast = toastData.element;

  // Clear any intervals
  if (toastData.interval) {
    clearInterval(toastData.interval);
  }

  // Add closing animation
  toast.classList.add('closing');

  // Remove after animation
  setTimeout(() => {
    if (toast.parentNode) {
      toast.parentNode.removeChild(toast);
    }
    activeToasts.delete(id);
  }, 300);
}

/**
 * Generate unique toast ID
 */
function generateToastId() {
  return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Remove all toasts
 */
export function clearAllToasts() {
  activeToasts.forEach((_, id) => {
    removeToast(id);
  });
}
